import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Crown, Check, Star, Zap, Play, Music, Image, Video,
  Sparkles, Gift, GraduationCap, X
} from 'lucide-react';
import { toast } from 'sonner';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { EditableList } from '@/components/edit/EditableList';
import { useHero } from '@/hooks/useHero';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    id: 'free',
    name: 'Free Level',
    price: 0,
    interval: 'month',
    description: 'Get started with basic access',
    icon: Zap,
    color: 'from-gray-500/20 to-slate-500/20',
    borderColor: 'border-gray-500/30',
    buttonColor: 'bg-gray-600 hover:bg-gray-700 text-white',
    badge: null,
    features: [
      { text: 'Access to cultural resources', included: true },
      { text: 'Limited access to pages', included: true },
      { text: 'Watch video trailers', included: true },
      { text: 'Purchase royalty-free music', included: true },
      { text: 'Purchase royalty-free photos', included: true },
      { text: 'Purchase royalty-free visual artwork', included: true },
      { text: 'First to see new videos before public release', included: false },
      { text: '"How I Did DaT" video series access', included: false },
      { text: 'Free trial: Private percussion course ($650 value)', included: true, highlight: true },
      { text: 'Free trial: Beginner Samba Dance Classes ($70 value)', included: true, highlight: true },
    ]
  },
  {
    id: 'watch-all',
    name: 'Watch All Videos',
    price: 2.99,
    interval: 'month',
    description: 'Stream all performance videos',
    icon: Play,
    color: 'from-blue-500/20 to-cyan-500/20',
    borderColor: 'border-blue-500/30',
    buttonColor: 'bg-blue-500 hover:bg-blue-600 text-white',
    badge: null,
    features: [
      { text: 'Access to cultural resources', included: true },
      { text: 'Limited access to pages', included: true },
      { text: 'Watch ALL featured videos & trailers', included: true, highlight: true },
      { text: 'Purchase royalty-free music', included: true },
      { text: 'Purchase royalty-free photos', included: true },
      { text: 'Purchase royalty-free visual artwork', included: true },
      { text: 'Purchase content licenses', included: true },
      { text: 'First to see new videos before public release', included: true },
      { text: '"How I Did DaT" video series access', included: true },
      { text: '7-day trial: General percussion course ($650 value)', included: true, highlight: true },
      { text: '7-day trial: Beginner Samba Dance Classes ($70 value)', included: true, highlight: true },
    ]
  },
  {
    id: 'silver',
    name: 'Silver Level',
    price: 9.99,
    interval: 'month',
    description: 'Full access to all content',
    icon: Star,
    color: 'from-slate-400/20 to-gray-300/20',
    borderColor: 'border-slate-400/50',
    buttonColor: 'bg-gradient-to-r from-slate-400 to-gray-300 hover:from-slate-500 hover:to-gray-400 text-black',
    badge: 'POPULAR',
    badgeColor: 'bg-slate-400 text-black',
    features: [
      { text: 'Access to cultural resources', included: true },
      { text: 'ALL access to ALL pages', included: true, highlight: true },
      { text: 'Watch ALL featured videos & trailers', included: true },
      { text: 'Purchase 4K trailers (no watermark)', included: true },
      { text: 'Purchase royalty-free music', included: true },
      { text: 'Purchase royalty-free photos', included: true },
      { text: 'Purchase royalty-free visual artwork', included: true },
      { text: 'Purchase content licenses', included: true },
      { text: 'First to see new videos before public release', included: true },
      { text: '"How I Did DaT" video series access', included: true },
      { text: '7-day trial: General percussion course ($650 value)', included: true, highlight: true },
      { text: '7-day trial: Beginner Samba Dance Classes ($70 value)', included: true, highlight: true },
    ]
  },
  {
    id: 'gold',
    name: 'Gold Level',
    price: 49.99,
    interval: 'month',
    description: 'Premium VIP experience',
    icon: Crown,
    color: 'from-yellow-500/20 to-amber-500/20',
    borderColor: 'border-yellow-500/50',
    buttonColor: 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black',
    badge: 'VIP',
    badgeColor: 'bg-yellow-500 text-black',
    features: [
      { text: 'Access to cultural resources', included: true },
      { text: 'ALL access to ALL pages', included: true },
      { text: 'Watch ALL featured videos & trailers', included: true },
      { text: 'Purchase 4K trailers (no watermark)', included: true },
      { text: 'Purchase royalty-free music', included: true },
      { text: 'Purchase royalty-free photos', included: true },
      { text: 'Purchase royalty-free visual artwork', included: true },
      { text: 'Purchase content licenses', included: true },
      { text: 'First to see new videos before public release', included: true, highlight: true },
      { text: '"How I Did DaT" video series access', included: true },
      { text: '7-day trial: General percussion course ($650 value)', included: true, highlight: true },
      { text: '7-day trial: Beginner Samba Dance Classes ($70 value)', included: true, highlight: true },
      { text: 'VIP priority support', included: true, highlight: true },
      { text: 'Exclusive Gold member content', included: true, highlight: true },
    ]
  }
];

const PlanCard = ({ plan, onSubscribe, isLoading }) => {
  const Icon = plan.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative bg-gradient-to-br ${plan.color} border-2 ${plan.borderColor} rounded-3xl p-6 flex flex-col h-full`}
    >
      {/* Badge */}
      {plan.badge && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 ${plan.badgeColor} rounded-full text-xs font-bold`}>
          {plan.badge}
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-6">
        <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br ${plan.color} border ${plan.borderColor} flex items-center justify-center mb-4`}>
          <Icon className="w-8 h-8 text-foreground" />
        </div>
        <h3 className="text-2xl font-bold font-display text-foreground">{plan.name}</h3>
        <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
      </div>

      {/* Price */}
      <div className="text-center mb-6">
        <div className="flex items-end justify-center gap-1">
          <span className="text-4xl font-bold text-foreground">
            ${plan.price === 0 ? '0' : plan.price}
          </span>
          <span className="text-muted-foreground mb-1">/{plan.interval}</span>
        </div>
      </div>

      {/* Features */}
      <div className="flex-1 space-y-3 mb-6">
        {plan.features.map((feature, idx) => (
          <div 
            key={idx} 
            className={`flex items-start gap-2 text-sm ${
              feature.included ? 'text-foreground' : 'text-muted-foreground/50'
            }`}
          >
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
              feature.included 
                ? feature.highlight ? 'text-primary' : 'text-green-500' 
                : 'text-muted-foreground/30'
            }`} />
            <span className={feature.highlight ? 'font-medium' : ''}>{feature.text}</span>
          </div>
        ))}
      </div>

      {/* CTA Button */}
      <button
        onClick={() => onSubscribe(plan)}
        disabled={isLoading}
        className={`w-full py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${plan.buttonColor}`}
        data-testid={`subscribe-${plan.id}`}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Processing...
          </span>
        ) : plan.price === 0 ? (
          'Get Started Free'
        ) : (
          'Subscribe Now'
        )}
      </button>
    </motion.div>
  );
};

export const Membership = () => (
  <PageEditProvider slug="membership">
    <MembershipInner />
    <PageEditToolbar />
  </PageEditProvider>
);

const MembershipInner = () => {
  const [isLoading, setIsLoading] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const hero = useHero('membership');

  const handleSubscribe = (plan) => {
    if (plan.price === 0) {
      // Free tier is open access — no signup needed. Honest copy so visitors
      // aren't tricked into thinking they "joined" something.
      toast.success('Free content is open to everyone — no signup needed. Create an account to unlock more.');
      return;
    }
    
    setSelectedPlan(plan);
    setShowEmailModal(true);
  };

  const handleCheckout = async (provider = 'stripe') => {
    if (!formData.email || !formData.name) {
      toast.error('Please enter your name and email');
      return;
    }

    setIsLoading(provider);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/memberships/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: formData.email,
          user_name: formData.name,
          plan_id: selectedPlan.id,
          plan_name: selectedPlan.name,
          payment_provider: provider
        })
      });

      const data = await response.json();
      
      if (response.ok && data.checkout_url) {
        // Redirect to checkout
        window.location.href = data.checkout_url;
      } else {
        console.error('Checkout error:', data);
        toast.error(data.detail || 'Failed to create checkout. Please try again.');
      }
    } catch (err) {
      console.error('Checkout exception:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(null);
      setShowEmailModal(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-amber-500/5" />
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-full text-primary text-sm font-medium mb-6">
              <Crown className="w-4 h-4" />
              <PageEditable path="hero.eyebrow" fallback="MEMBERSHIP PLANS" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-foreground mb-6">
              <PageEditable path="hero.titlePre" fallback="Choose Your" /> <span className="text-primary"><PageEditable path="hero.titleAccent" fallback="Plan" /></span>
            </h1>
            <PageEditable
              path="hero.subtitle"
              as="p"
              multiline
              className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4"
              fallback={hero.subtitle || "Get access to exclusive Afro-Brasilian percussion performances, courses, royalty-free content, and more from Mestre Curtis Pierre."}
            />
            <PageEditable
              path="hero.microcopy"
              as="p"
              className="text-sm text-muted-foreground"
              fallback="Cancel anytime. No hidden fees."
            />
          </motion.div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <PlanCard 
                key={plan.id} 
                plan={plan} 
                onSubscribe={handleSubscribe}
                isLoading={isLoading === plan.id}
              />
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-background to-card">
        <div className="max-w-4xl mx-auto text-center">
          <PageEditable
            path="whatYouGet.title"
            as="h2"
            className="text-3xl font-bold font-display text-foreground mb-12"
            fallback="What You Get With Your Membership"
          />
          <EditableList
            path="benefits"
            fallback={[
              { icon: 'Video', title: '4K Performance Videos', desc: 'Watch stunning live percussion performances shot in 4K video format' },
              { icon: 'Music', title: 'Royalty-Free Content', desc: 'Access music, photos, and artwork for your own creative projects' },
              { icon: 'GraduationCap', title: 'Free Course Trials', desc: 'Try percussion and samba dance classes worth over $700' },
            ]}
            emptyItem={() => ({ title: 'New benefit', desc: 'Describe this benefit' })}
            className="grid md:grid-cols-3 gap-8"
            renderItem={(b, idx) => {
              const Icon = b.icon === 'Music' ? Music : b.icon === 'GraduationCap' ? GraduationCap : Video;
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-4">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground mb-2">
                    <PageEditable path={`benefits.${idx}.title`} as="span" fallback={b.title} />
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    <PageEditable
                      path={`benefits.${idx}.desc`}
                      as="span"
                      multiline
                      fallback={b.desc}
                    />
                  </p>
                </motion.div>
              );
            }}
          />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold font-display text-foreground text-center mb-12">
            <PageEditable path="faq.title" as="span" fallback="Frequently Asked Questions" />
          </h2>
          <EditableList
            path="faq.items"
            fallback={[
              { q: "Can I cancel my subscription anytime?", a: "Yes! You can cancel your subscription at any time. Your access will continue until the end of your billing period." },
              { q: "What payment methods do you accept?", a: "We accept all major credit cards (Visa, MasterCard, American Express) as well as Apple Pay and Google Pay through Stripe." },
              { q: "Can I upgrade or downgrade my plan?", a: "Absolutely! You can change your plan at any time. If you upgrade, you'll be charged the prorated difference. If you downgrade, the change takes effect at your next billing date." },
              { q: "Do I get access to the courses with my membership?", a: "Membership includes free trial access to courses. Full course access and certifications are available for separate purchase." }
            ]}
            emptyItem={() => ({ q: 'New question?', a: 'Answer goes here.' })}
            className="space-y-6"
            renderItem={(faq, idx) => (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-card border border-border/50 rounded-2xl p-6"
              >
                <h3 className="font-bold text-foreground mb-2">
                  <PageEditable path={`faq.items.${idx}.q`} as="span" fallback={faq.q} />
                </h3>
                <p className="text-muted-foreground text-sm">
                  <PageEditable path={`faq.items.${idx}.a`} as="span" multiline fallback={faq.a} />
                </p>
              </motion.div>
            )}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 rounded-3xl p-8 md:p-12 text-center"
          >
            <Crown className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
            <PageEditable
              path="cta.title"
              as="h2"
              className="text-3xl font-bold font-display text-foreground mb-4"
              fallback="Start Your Journey Today"
            />
            <PageEditable
              path="cta.description"
              as="p"
              multiline
              className="text-muted-foreground mb-8 max-w-xl mx-auto"
              fallback="Join the Escola de Samba Casa Samba community and immerse yourself in the world of Afro-Brasilian percussion with Mestre Curtis Pierre."
            />
            <button
              onClick={() => document.querySelector('[data-testid="subscribe-gold"]')?.click()}
              className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black rounded-full font-bold text-lg transition-all hover:scale-105 shadow-glow"
            >
              <PageEditable path="cta.button" fallback="Get Gold Membership" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Email Modal for Checkout */}
      {showEmailModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-card border border-border/50 rounded-2xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-foreground">Subscribe to {selectedPlan.name}</h2>
                <p className="text-muted-foreground text-sm">${selectedPlan.price}/month</p>
              </div>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Your Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="checkout-name"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="checkout-email"
                />
              </div>

              <button
                onClick={() => handleCheckout('stripe')}
                disabled={isLoading}
                className={`w-full py-4 rounded-full font-bold text-lg transition-all hover:scale-105 ${selectedPlan.buttonColor} disabled:opacity-50`}
              >
                {isLoading === 'stripe' ? 'Processing...' : `Pay with Card - $${selectedPlan.price}/mo`}
              </button>

              <button
                onClick={() => handleCheckout('paypal')}
                disabled={isLoading}
                className="w-full py-4 rounded-full font-bold text-lg transition-all hover:scale-105 bg-[#0070ba] hover:bg-[#003087] text-white disabled:opacity-50"
              >
                {isLoading === 'paypal' ? 'Processing...' : `Pay with PayPal - $${selectedPlan.price}/mo`}
              </button>

              <p className="text-xs text-muted-foreground text-center">
                Secure payment. Cancel anytime.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Membership;
